import { useThrottleFn } from '@vueuse/core';
import { Ref, ref, shallowRef, unref } from 'vue';
import { isDef } from '@mts-fe/utils';
import { adaptiveSize } from '@mts/utils';
import { PCMode } from '@mts/utils/env';

const watermarkSymbol = 'watermark-dom';
const updateWatermarkText = ref<string | null>(null);

export interface UseWatermarkRes {
  setWatermark: (str: string) => void;
  clear: () => void;
  clearAll: () => void;
  waterMarkOptions?: waterMarkOptionsType;
  obInstance?: MutationObserver;
  targetElement?: HTMLElement;
  parentElement?: HTMLElement;
}

interface waterMarkOptionsType {
  // 自定义水印的文字大小
  fontSize?: number;
  // 自定义水印的文字颜色
  fontColor?: string;
  // 自定义水印的文字字体
  fontFamily?: string;
  // 自定义水印的文字对齐方式
  textAlign?: CanvasTextAlign;
  // 自定义水印的文字基线
  textBaseline?: CanvasTextBaseline;
  // 自定义水印的文字倾斜角度
  rotate?: number;
}

const sourceMap = new Map<symbol, Omit<UseWatermarkRes, 'clearAll'>>();

function findTargetNode(el) {
  return Array.from(sourceMap.values()).find((item) => item.targetElement === el);
}

function createBase64(str: string, waterMarkOptions: waterMarkOptionsType) {
  const can = document.createElement('canvas');
  const width = adaptiveSize(300);
  const height = adaptiveSize(240);
  Object.assign(can, { width, height });

  const cans = can.getContext('2d');
  if (cans) {
    const fontFamily = waterMarkOptions?.fontFamily || 'Vedana';
    const fontSize = adaptiveSize(waterMarkOptions?.fontSize || 12);
    const fontColor = waterMarkOptions?.fontColor || 'rgba(0, 0, 0, 0.1)';
    const textAlign = waterMarkOptions?.textAlign || 'left';
    const textBaseline = waterMarkOptions?.textBaseline || 'middle';
    const rotate = waterMarkOptions?.rotate || 20;
    cans.rotate((-rotate * Math.PI) / 180);
    cans.font = `${fontSize}px ${fontFamily}`;
    cans.fillStyle = fontColor;
    cans.textAlign = textAlign;
    cans.textBaseline = textBaseline;
    cans.fillText(str, width / 20, height);
  }
  return can.toDataURL('image/png');
}

const obFn = () => {
  const obInstance = new MutationObserver((mutationRecords) => {
    for (const mutation of mutationRecords) {
      for (const node of Array.from(mutation.removedNodes)) {
        const target = findTargetNode(node);
        if (!target) return;
        const { targetElement, parentElement } = target;
        // 父元素的子元素水印如果被删除 重新插入被删除的水印(防篡改，插入通过控制台删除的水印)
        if (!parentElement?.contains(targetElement as Node | null)) {
          target?.parentElement?.appendChild(node as HTMLElement);
        }
      }
      if (mutation.type === 'attributes' && mutation.target) {
        // 修复控制台可以”Hide element” 的问题
        const _target = mutation.target as HTMLElement;
        const target = findTargetNode(_target);
        if (target) {
          // 禁止改属性 包括class 修改以后 mutation.type 也等于 'attributes'
          // 先解除监听 再加一下
          clearAll();
          target.setWatermark(target.targetElement?.['data-watermark-text']);
        }
      }
    }
  });
  return obInstance;
};

export function useWatermark(
  appendEl: Ref<HTMLElement | null> = shallowRef(document.body) as Ref<HTMLElement>,
  waterMarkOptions: waterMarkOptionsType = {},
): UseWatermarkRes {
  const domSymbol = Symbol(watermarkSymbol);
  const appendElRaw = unref(appendEl);

  if (appendElRaw && sourceMap.has(domSymbol)) {
    const { setWatermark, clear } = sourceMap.get(domSymbol) as UseWatermarkRes;
    return { setWatermark, clear, clearAll };
  }

  const watermarkEl = shallowRef<HTMLElement>();

  const clear = () => {
    const domId = unref(watermarkEl);
    if (!domId) return;

    watermarkEl.value = undefined;
    const el = unref(appendEl);

    const instance = sourceMap.get(domSymbol);
    if (instance) {
      instance.obInstance?.disconnect();
      sourceMap.delete(domSymbol);
    }

    if (!el || !domId) return;

    if (el.contains(domId)) {
      el.removeChild(domId);
    }
  };

  function updateWatermark(
    options: {
      width?: number;
      height?: number;
      str?: string;
    } = {},
  ) {
    const el = unref(watermarkEl);
    if (!el) return;

    let styleText = '';
    if (isDef(options.width)) {
      styleText += `width:${options.width}px;`;
    }
    if (isDef(options.height)) {
      styleText += `height:${options.height}px;`;
    }

    if (styleText) {
      el.style.cssText += styleText;
    }

    if (isDef(options.str)) {
      const bgImage = createBase64(options.str, waterMarkOptions);
      el.style.background = `url(${bgImage}) left top repeat`;
    }
  }

  const createWatermark = (str: string) => {
    const existingEl = unref(watermarkEl);

    if (existingEl && sourceMap.has(domSymbol)) {
      const existingText = existingEl['data-watermark-text'];
      if (existingText === str) {
        return;
      }
      updateWatermarkText.value = str;
      existingEl['data-watermark-text'] = str;
      updateWatermark({ str });
      return;
    }

    const div = document.createElement('div');
    div['data-watermark-text'] = str;
    updateWatermarkText.value = str;

    const baseStyle = `pointer-events:none;display:block;visibility:visible;top:0;left:0;position:absolute;z-index:100000;height:100%;width:100%;`;
    const bgImage = createBase64(str, waterMarkOptions);
    div.style.cssText = `${baseStyle}background:url(${bgImage}) left top repeat;`;
    div.className = '__' + watermarkSymbol;

    watermarkEl.value = div;

    const el = unref(appendEl);
    if (!el) return;

    const { clientHeight: height, clientWidth: width } = el;

    if (width && height) {
      div.style.width = `${width}px`;
      div.style.height = `${height}px`;
    }

    el.appendChild(div);

    const instance = {
      setWatermark,
      clear,
      parentElement: el,
      targetElement: div,
      obInstance: PCMode ? obFn() : void 0,
      waterMarkOptions,
    };
    sourceMap.set(domSymbol, instance);

    requestAnimationFrame(() => {
      instance.obInstance?.observe(el, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    });
  };

  function setWatermark(str: string) {
    createWatermark(str);
  }

  const resizeHandler = useThrottleFn(() => {
    const el = unref(appendEl);
    if (!el) return;
    const { clientHeight: height, clientWidth: width } = el;
    updateWatermark({ height, width });
  }, 1000);
  new ResizeObserver(resizeHandler).observe(appendElRaw!);

  return { setWatermark, clear, clearAll };
}

function clearAll() {
  Array.from(sourceMap.values()).forEach((item) => {
    item?.obInstance?.disconnect();
    item.clear();
  });
}
